import axiosInstance from "../helpers/axiosInstance";

const apiEndpoint = "/api/feeds";

/**
 * Fetches all custom feeds for a specific user.
 *
 * @param {string} userId - The ID of the user to fetch feeds for.
 * @returns {Promise<Object>} A promise that resolves with the paginated response containing feeds.
 * @throws {Error} Throws an error if the request fails.
 */
export async function getUserFeeds(userId) {
    const response = await axiosInstance.get(`${apiEndpoint}/${userId}`);
    return response.data;
}

/**
 * Creates a new custom feed for a user.
 *
 * @param {string} userId - The ID of the user creating the feed.
 * @param {Object} feedData - The feed data to create.
 * @param {string} feedData.name - The name of the feed.
 * @param {Array<string>} feedData.zipcodes - Array of zipcodes for the feed.
 * @param {Array<string>} feedData.type - The types of properties to include.
 * @param {Object} feedData.price - The price range.
 * @param {string} feedData.price.min - The minimum price.
 * @param {string} feedData.price.max - The maximum price.
 * @param {Object} feedData.beds - The number of bedrooms range.
 * @param {number} feedData.beds.min - The minimum number of bedrooms.
 * @param {number} feedData.beds.max - The maximum number of bedrooms.
 * @param {Object} feedData.bath - The number of bathrooms range.
 * @param {number} feedData.bath.min - The minimum number of bathrooms.
 * @param {number} feedData.bath.max - The maximum number of bathrooms.
 * @returns {Promise<Object>} A promise that resolves with the created feed.
 * @throws {Error} Throws an error if the request fails.
 */
export async function createUserFeed(userId, feedData) {
    const response = await axiosInstance.post(`${apiEndpoint}/${userId}`, feedData);
    return response.data;
}

/**
 * Updates an existing custom feed for a user.
 *
 * @param {string} userId - The ID of the user updating the feed.
 * @param {Object} feedData - The updated feed data.
 * @param {string} feedData.id - The ID of the feed to update.
 * @param {string} feedData.name - The name of the feed.
 * @param {Array<string>} feedData.zipcodes - Array of zipcodes for the feed.
 * @param {Array<string>} feedData.type - The types of properties to include.
 * @param {Object} feedData.price - The price range.
 * @param {string} feedData.price.min - The minimum price.
 * @param {string} feedData.price.max - The maximum price.
 * @param {Object} feedData.beds - The number of bedrooms range.
 * @param {number} feedData.beds.min - The minimum number of bedrooms.
 * @param {number} feedData.beds.max - The maximum number of bedrooms.
 * @param {Object} feedData.bath - The number of bathrooms range.
 * @param {number} feedData.bath.min - The minimum number of bathrooms.
 * @param {number} feedData.bath.max - The maximum number of bathrooms.
 * @returns {Promise<Object>} A promise that resolves with the updated feed.
 * @throws {Error} Throws an error if the request fails.
 */
export async function updateUserFeed(userId, feedData) {
    const response = await axiosInstance.put(`${apiEndpoint}/${userId}/${feedData.id}`, feedData);
    return response.data;
}

/**
 * Deletes a custom feed for a user.
 *
 * @param {string} userId - The ID of the user deleting the feed.
 * @param {string} feedId - The ID of the feed to delete.
 * @returns {Promise<Object>} A promise that resolves with the deletion confirmation.
 * @throws {Error} Throws an error if the request fails.
 */
export async function deleteUserFeed(userId, feedId) {
    const response = await axiosInstance.delete(`${apiEndpoint}/${userId}/${feedId}`);
    return response.data;
}

/**
 * Fetches available zip codes for feeds
 * @returns {Promise<string[]>} A promise that resolves with an array of zip codes
 */
export const getFeedZipCodes = async () => {
    return (await axiosInstance.get("/api/feeds/zipcodes")).data;
};
